import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { getApiErrorMessage } from '../constants/messages';
import { Button, Input } from '../components/ui';

const VerifyAccountPage: React.FC = () => {
  const { t } = (useTranslation as any)('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { email?: string; orgId?: string } | null;
  const emailParam = locationState?.email || null;
  const orgIdParam = locationState?.orgId || null;

  const [email] = useState(emailParam || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!emailParam) {
      toast.error(t('verifyAccount.missingEmail'));
      navigate('/login');
    }
  }, [emailParam, navigate, t]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t('verifyAccount.invalidCode'));
      return;
    }

    setVerifying(true);
    try {
      await apiService.verifyAccount({
        email,
        token: verificationCode,
      });

      toast.success(t('verifyAccount.success'));
      setVerified(true);

      // Wait 2 seconds then redirect
      setTimeout(() => {
        if (orgIdParam) {
          // If coming from organization invitation, redirect to org dashboard
          navigate(`/organisations/${orgIdParam}/dashboard`);
        } else {
          // Otherwise go to organisations list
          navigate('/organisations');
        }
      }, 2000);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('verifyAccount.verifyError'));
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await apiService.resendVerification(email);
      toast.success(t('verifyAccount.resendSuccess'));
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('verifyAccount.resendError'));
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-0 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-surface-2 p-8 rounded-card shadow-card">
          <div className="text-center">
            <CheckCircleIcon className="mx-auto h-24 w-24 text-green-400" />
            <h2 className="mt-6 text-3xl font-extrabold text-text-primary">
              {t('verifyAccount.successTitle')}
            </h2>
            <p className="mt-2 text-sm text-text-secondary">
              {t('verifyAccount.successMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-0 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-surface-2 p-8 rounded-card shadow-card">
        <div>
          <div className="flex justify-center">
            <EnvelopeIcon className="h-16 w-16 text-accent-400" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-text-primary">
            {t('verifyAccount.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-text-secondary">
            {t('verifyAccount.subtitle')} <br />
            <span className="font-medium text-accent-400">{email}</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="mt-8 space-y-6">
          <div>
            <Input
              id="verificationCode"
              type="text"
              label={t('verifyAccount.codeLabel')}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              value={verificationCode}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/\D/g, '');
                setVerificationCode(value);
              }}
              className="text-center tracking-widest font-mono text-lg"
              placeholder={t('verifyAccount.codePlaceholder')}
              autoComplete="off"
            />
            <p className="mt-2 text-xs text-text-tertiary">
              {t('verifyAccount.codeHint')}
            </p>
          </div>

          <div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={verifying}
              disabled={verifying || verificationCode.length !== 6}
              className="w-full"
            >
              {verifying ? t('verifyAccount.verifying') : t('verifyAccount.submit')}
            </Button>
          </div>

          <div className="text-center">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleResendCode}
              disabled={resending}
              loading={resending}
              className="bg-transparent hover:bg-transparent text-accent-400 hover:text-accent-300"
            >
              {resending ? t('verifyAccount.resending') : t('verifyAccount.resend')}
            </Button>
          </div>
        </form>

        <div className="mt-6 border-t border-border-1 pt-6">
          <p className="text-xs text-text-tertiary text-center">
            {t('verifyAccount.expiryInfo')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyAccountPage;
